import { CalendarDate, getLocalTimeZone, today } from "@internationalized/date";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  createCardView,
  fetchCountries,
  fetchCurrencyOptions,
  fetchOpportunityTypes,
  fetchPurseOptions,
  fetchRosterStatus,
  fetchTrainingDisciplines, 
  fetchWeightClasses,
  getAllMarketplaceCards,
} from "../../services/api";
import { CardViewRequestDto, MarketplaceCardResponseDto } from "../../types/types";

export const useMarketplaceCards = () => {
  const { t, i18n } = useTranslation("marketPlaceCards");
  const tRef = useRef(t);
  const loginResponse = JSON.parse(localStorage.getItem("loginResponse") || "{}");
  const profileType = loginResponse?.profileType;
  const username = loginResponse?.username;
  const managerId = loginResponse?.id;

  const [activeTab, setActiveTab] = useState<"all" | "myCards">("all");
  const [currentMonth, setCurrentMonth] = useState<CalendarDate>(today(getLocalTimeZone()));
  const [currencies, setCurrencies] = useState<string[]>([]);
  const [confirmContactPopup, setConfirmContactPopup] = useState(false);
  const [marketplaceId, setMarketplaceId] = useState<number | null>(null);
  const [rosterStatus, setRosterStatus] = useState<string[]>([]);
  const [selectedRosterStatus, setSelectedRosterStatus] = useState("");
  const [selectedTitle, setSelectedTitle] = useState("");
  const [searchName, setSearchName] = useState("");
  const [filterCardType, setFilterCardType] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [filterSport, setFilterSport] = useState("");
  const [filterWeightClass, setFilterWeightClass] = useState("");
  const [filterPurse, setFilterPurse] = useState("");
  const [filterCurrency, setFilterCurrency] = useState("");
  const [filterOpportunityType, setFilterOpportunityType] = useState("");
  const [cards, setCards] = useState<MarketplaceCardResponseDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [showReportPopup, setShowReportPopup] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 12;
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<MarketplaceCardResponseDto | null>(null);
  const [viewCardId, setViewCardId] = useState<string | number | null>(null);
  const [isOpportunityViewOpen, setIsOpportunityViewOpen] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [confirmPublishPopup, setConfirmPublishPopup] = useState(false);
  const [locations, setLocations] = useState<string[]>([]);
  const [sports, setSports] = useState<string[]>([]);
  const [weightClasses, setWeightClasses] = useState<string[]>([]);
  const [purses, setPurses] = useState<string[]>([]);
  const [opportunityTypes, setOpportunityTypes] = useState<string[]>([]);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  useEffect(() => {
    const savedLanguage = localStorage.getItem("i18nextLng") || "en";
    i18n.changeLanguage(savedLanguage);
  }, [i18n]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const countries = await fetchCountries();
        setLocations(countries);
      } catch (e) {
        toast.error(tRef.current("failed_to_load_initial_data"));
      }
    };
    loadInitialData();
  }, []);

  const loadPursesIfNeeded = async () => {
    if (purses.length === 0) {
      try {
        const data = await fetchPurseOptions();
        setPurses(data);
      } catch (error) {
        toast.error(t("failed_to_load_purse_options"));
      }
    }
  };

  const loadSportsIfNeeded = async () => {
    if (sports.length === 0) {
      try {
        const data = await fetchTrainingDisciplines();
        setSports(data);
      } catch (error) {
        toast.error(t("failed_to_load_training_disciplines"));
      }
    }
  };

  const loadCurrenciesIfNeeded = async () => {
    if (currencies.length === 0) {
      try {
        const data = await fetchCurrencyOptions();
        setCurrencies(data);
      } catch (error) {
        toast.error(t("failed_to_load_currency_options"));
      }
    }
  };

  const loadWeightClassesIfNeeded = async () => {
    if (weightClasses.length === 0) {
      try {
        const data = await fetchWeightClasses();
        setWeightClasses(data);
      } catch (error) {
        toast.error(t("failed_to_load_weight_classes"));
      }
    }
  };

  const loadOpportunityTypesIfNeeded = async () => {
    if (opportunityTypes.length === 0) {
      const data = await fetchOpportunityTypes();
      setOpportunityTypes(data);
    }
  };

  const loadRosterStatusesIfNeeded = useCallback(async () => {
    if (rosterStatus.length === 0) {
      try {
        const rosterStatusData = await fetchRosterStatus();
        setRosterStatus(rosterStatusData);
      } catch (e) {
        toast.error(tRef.current("failed_to_load_roster_statuses"));
      }
    }
  }, [rosterStatus.length]);

  useEffect(() => {
    const loadAllFilters = async () => {
      await Promise.all([
        loadPursesIfNeeded(),
        loadSportsIfNeeded(),
        loadCurrenciesIfNeeded(),
        loadWeightClassesIfNeeded(),
        loadOpportunityTypesIfNeeded(),
        loadRosterStatusesIfNeeded(),
      ]);
    };
    loadAllFilters();
  }, []);

  const fetchMarketplaceCards = useCallback(async () => {
    setLoading(true);
    try {
      const filters: any = { 
        name: searchName || undefined,
        role: filterCardType || undefined,
        location: filterLocation || undefined,
        sport: filterSport || undefined,
        weightClass: filterWeightClass || undefined,
        purse: filterPurse || undefined,
        currency: filterCurrency || undefined,
        opportunityType: filterOpportunityType || undefined,
        rosterStatus: selectedRosterStatus || undefined,
        titleShot: selectedTitle === "All Titles" ? undefined : selectedTitle,
        myCard: activeTab === "myCards" ? true : undefined,
        page: currentPage - 1,
        size: pageSize,
      };

      const data = await getAllMarketplaceCards(username || "", filters);

      if (data && data.content) {
        setCards(data.content);
        setTotalPages(data.totalPages || 1);
      } else {
        setCards([]);
        setTotalPages(1);
      }
    } catch (e) {
      toast.error(tRef.current("failed_to_load_cards"));
      setCards([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [
    searchName,
    filterCardType,
    filterLocation,
    filterSport,
    filterWeightClass,
    filterPurse,
    filterCurrency,
    filterOpportunityType,
    selectedRosterStatus,
    selectedTitle,
    currentPage,
    activeTab,
    username,
  ]);

  useEffect(() => {
    fetchMarketplaceCards();
  }, [fetchMarketplaceCards]);

  const trackGeoLocation = async (viewedCardId?: string | number) => {
    try {
      if (!viewedCardId) return;
      const storageKey = `lastViewed_${viewedCardId}`;
      const lastViewed = localStorage.getItem(storageKey);
      if (lastViewed && Date.now() - Number(lastViewed) < 5 * 60 * 1000) return;

      const geoRes = await fetch("https://ipapi.co/json");
      const geoData = await geoRes.json();

      const userJson = localStorage.getItem("loginResponse");
      const user = userJson ? JSON.parse(userJson) : null;
      if (!user?.username) return;

      const dto: CardViewRequestDto = {
        marketplaceCardId: viewedCardId,
        informationRequestDto: {
          ipAddress: geoData.ip,
          countryCode: geoData.country_code,
          city: geoData.city,
          browserInfo: navigator.userAgent,
          sourcePage: window.location.pathname,
        },
      };
      await createCardView(user.username, dto);
      localStorage.setItem(storageKey, Date.now().toString());
    } catch (error: any) {}
  };

  const handleResetFilters = useCallback(() => {
    setSearchName("");
    setFilterCardType("");
    setFilterLocation("");
    setFilterSport("");
    setFilterWeightClass("");
    setFilterPurse("");
    setFilterCurrency("");
    setFilterOpportunityType("");
    setSelectedRosterStatus("");
    setSelectedTitle("");
    setCurrentPage(1);
  }, [t]);

  const handleViewModalClose = useCallback(() => {
    setIsViewModalOpen(false);
    setSelectedCard(null);
    setConfirmPublishPopup(false);
  }, []);

  const handlePublish = useCallback(
    async (cardId: string) => {
      if (isPublishing) return;
      setIsPublishing(false);
    },
    [isPublishing]
  );

  const handleDelete = useCallback(async () => {}, []);

  const handleView = useCallback(
    (card: MarketplaceCardResponseDto) => {
      try {
        setSelectedCard(card);
        setIsViewModalOpen(true);
        trackGeoLocation(card.id);
      } catch (error) {
        toast.error(t("failed_to_open_view_modal"));
      }
    },
    [t]
  );

  const handleOpportunityView = useCallback(
    (cardId: string | number) => {
      try {
        setViewCardId(cardId);
        setIsOpportunityViewOpen(true);
      } catch (error) {
        toast.error(t("failed_to_open_opportunity_view"));
      }
    },
    [t]
  );

  const handleCreateContact = useCallback(async () => {}, []);

  return {
    t,
    profileType,
    username,
    managerId,
    loginResponse,
    activeTab,
    setActiveTab,
    currentMonth,
    setCurrentMonth,
    currencies,
    confirmContactPopup,
    setConfirmContactPopup,
    marketplaceId,
    rosterStatus,
    selectedRosterStatus,
    setSelectedRosterStatus,
    selectedTitle,
    setSelectedTitle,
    searchName,
    setSearchName,
    filterCardType,
    setFilterCardType,
    filterLocation,
    setFilterLocation,
    filterSport,
    setFilterSport,
    filterWeightClass,
    setFilterWeightClass,
    filterPurse,
    setFilterPurse,
    filterCurrency,
    setFilterCurrency,
    filterOpportunityType,
    setFilterOpportunityType,
    cards,
    loading,
    showReportPopup,
    setShowReportPopup,
    currentPage,
    setCurrentPage,
    totalPages,
    isViewModalOpen,
    selectedCard,
    viewCardId,
    isOpportunityViewOpen,
    setIsOpportunityViewOpen,
    isPublishing,
    confirmPublishPopup,
    setConfirmPublishPopup,
    locations,
    sports,
    weightClasses,
    purses,
    opportunityTypes,
    fetchMarketplaceCards,
    handleResetFilters,
    handleViewModalClose,
    handlePublish,
    handleDelete,
    handleView,
    handleOpportunityView,
    handleCreateContact,
    loadPursesIfNeeded,
    loadCurrenciesIfNeeded,
    loadWeightClassesIfNeeded,
    loadSportsIfNeeded,
  };
};
